import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
// Redux
import { RootState } from "app/store";
import { useAppSelector } from "app/hooks/useStore";
import { selectDocumentsAsOptions } from "app/store/DMSDocuments/DMSDocuments.selectors";
// Mui
import { Box, Chip, Stack, Divider } from "@mui/material";
// Components
import { Autocomplete } from 'app/components/Mui/Autocompletes';

export default function DialogFilterSelectors({
  initialDocumentIds,
}: {
  initialDocumentIds: (string|number)[];
}) {
  const { t } = useTranslation();

  const documentOptions = useAppSelector((state:RootState) => selectDocumentsAsOptions(state, {
    excludeCustomFormat: false,
    onlyOcred: true
  }));

  const { control } = useFormContext();

  return (
    <Fragment>
      <Divider />
      <Stack direction="row" spacing={2}>
        <Controller
          control={control} name="documentIds"
          render={({ field:{ onChange, ...restFields } }) => (
            <Autocomplete
              {...restFields}
              options={[
                { value: 'all', label: t('labels.all') },
                ...documentOptions
              ]}
              onChange={(nextValue, reason) => {
                let newValue = nextValue || initialDocumentIds;

                if ( nextValue && Array.isArray(nextValue) ){
                  if ( reason === 'removeOption' || reason === 'clear' ){
                    if ( !nextValue || !nextValue.length ) newValue = initialDocumentIds;
                  }
                  if ( reason === 'selectOption' ){
                    const [ firstAllOption, ...otherOptions ] = nextValue;
                    const lastAllOption = nextValue.at(-1)
                    if ( firstAllOption === 'all' ){
                      newValue = otherOptions
                    } else if ( lastAllOption === 'all' ){
                      newValue = initialDocumentIds;
                    }
                  }
                }

                onChange(newValue)
              }}
              TextFieldProps={{
                sx: { minWidth: 240 },
                label: t('labels.documents'),
                name: 'documentIds',
                margin: 'none',
                size: 'small',
                fullWidth: false
              }}
              renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => {
                const { onDelete, ...otherTagPros } = getTagProps({ index });
                return (
                  <Chip
                    {...otherTagPros}
                    key={option.value}
                    style={{ maxWidth: '160px' }}
                    label={option.label}
                    title={option.label}
                    size="small"
                    onDelete={option.value === 'all' ? undefined : onDelete}
                  />
                )
              })}
              groupBy={(option) => option.props?.collection?.name || 'all'}
              renderGroup={(params) => {
                if ( params.group === 'all' ) return (
                  <Box key={params.key}>{params.children}</Box>
                );
                return (
                  <Box key={params.key}>
                    <Box sx={{
                      bgcolor: 'rgb(238,238,238)',
                      color: 'rgba(0,0,0,0.87)',
                      fontWeight: 700,
                      py: 2,
                      px: 4
                    }}>{params.group}</Box>
                    {params.children}
                  </Box>
                )
              }}
              size="small"
              multiple={true}
              limitTags={3}
              filterSelectedOptions
            />
          )}
        />
      </Stack>
    </Fragment>
  );
}
